/**
 * 工作区相关的React Hooks
 * 基于SWR提供工作区管理相关的状态管理和API调用功能
 */

import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import { API_PATHS, spaceService } from '../services/workspace.service'
import type {
  Workspace,
  GetWorkspaceMembersRequest,
  WorkspaceMemberListRes,
  RemoveMemberRequest,
  GenerateInviteLinkRequest,
  InvitationLinkRes,
  InviteMsgRes,
  AcceptInviteRequest,
  TransferWorkspaceRequest,
  DeleteWorkspaceRequest,
  LeaveWorkspaceRequest,
} from '../types/workspace.types'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useUserStore } from '@/stores/userStore'
import { useEffect } from 'react'

interface GetWorkspacesResponse {
  workspaces: Workspace[]
}

type UpdateWorkspaceRequest = {
  name: string
  icon: string
  id?: string
}

/**
 * 获取工作区列表Hook
 * @returns SWR查询对象，包含data、error、mutate等
 */
export const useWorkspaces = () => {
  const { currentUser } = useUserStore()
  // SWR用于数据获取和缓存，只有当用户登录时才进行请求
  const { data, error, mutate } = useSWR<GetWorkspacesResponse>(
    currentUser ? API_PATHS.LIST : null,
    spaceService.getWorkspaces
  )
  const { setWorkspaces, currentWorkspace, setCurrentWorkspace } =
    useWorkspaceStore()

  useEffect(() => {
    if (data?.workspaces) {
      console.log('获取到工作区列表，正在更新Store:', data.workspaces)
      setWorkspaces(data.workspaces)

      // 处理当前选中空间的逻辑
      if (!currentWorkspace && data.workspaces.length > 0) {
        // 如果没有当前空间，选择第一个空间
        console.log('没有当前选中空间，选择第一个空间:', data.workspaces[0])
        setCurrentWorkspace(data.workspaces[0])
      } else if (currentWorkspace) {
        // 如果有当前空间，检查它是否还在新的列表中
        const updatedCurrentWorkspace = data.workspaces.find(
          (workspace) => workspace.id === currentWorkspace.id
        )

        if (updatedCurrentWorkspace) {
          // 如果找到了，更新当前空间信息（可能名称或图标有变化）
          console.log('更新当前选中空间信息:', updatedCurrentWorkspace)
          setCurrentWorkspace(updatedCurrentWorkspace)
        } else {
          // 如果当前空间不在新列表中（可能被删除或用户权限变化），选择第一个空间
          console.log(
            '当前选中空间已不存在，选择第一个空间:',
            data.workspaces[0]
          )
          if (data.workspaces.length > 0) {
            setCurrentWorkspace(data.workspaces[0])
          } else {
            setCurrentWorkspace(null)
          }
        }
      }
    }
  }, [data, setWorkspaces, currentWorkspace, setCurrentWorkspace])

  // 当用户状态变化时（重新登录），重新获取空间列表
  // useEffect(() => {
  //   if (currentUser) {
  //     console.log('用户登录状态变化，重新获取工作区列表')
  //     mutate()
  //   }
  // }, [currentUser?.id, mutate]) // 监听用户ID变化

  return { data, error, mutate }
}

/**
 * 更新工作区Hook
 * @returns SWR mutation对象，包含trigger（更新函数）、isMutating（加载状态）、error（错误信息）
 */
export const useUpdateWorkspace = () => {
  return useSWRMutation<void, Error, string, UpdateWorkspaceRequest>(
    API_PATHS.UPDATE,
    (_, { arg }) => spaceService.updateWorkspace(arg)
  )
}

/**
 * 获取工作区成员列表Hook
 * @param params 查询参数
 * @returns SWR查询对象，包含成员列表数据
 */
export const useWorkspaceMembers = (params: GetWorkspaceMembersRequest) => {
  // 构建唯一的key，包含所有查询参数
  // 只有当workspaceId大于0时才进行请求
  const key =
    params.workspaceId && params.workspaceId > 0
      ? [API_PATHS.MEMBER_LIST, params]
      : null

  return useSWR<WorkspaceMemberListRes>(
    key,
    () => spaceService.getWorkspaceMembers(params),
    {
      // 只有当workspaceId存在时才进行请求
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      // 添加缓存配置
      dedupingInterval: 5000, // 5秒内的重复请求会被去重
      keepPreviousData: true, // 在加载新数据时保留旧数据
      revalidateIfStale: false, // 如果数据过期，不自动重新验证
      revalidateOnMount: true, // 组件挂载时重新验证
    }
  )
}

/**
 * 移除工作区成员Hook
 * @returns SWR mutation对象，包含trigger（移除函数）、isMutating（加载状态）、error（错误信息）
 */
export const useRemoveMember = () => {
  return useSWRMutation<null, Error, string, RemoveMemberRequest>(
    API_PATHS.REMOVE_MEMBER,
    (_, { arg }) => spaceService.removeMember(arg)
  )
}

/**
 * 生成邀请链接Hook
 * @param workspaceId 工作区ID
 * @returns SWR查询对象，包含邀请链接数据
 */
export const useGenerateInviteLink = (workspaceId?: string) => {
  // 构建唯一的key，只有当workspaceId存在时才进行请求
  const key = workspaceId ? [API_PATHS.GENERATE_INVITE_LINK, workspaceId] : null

  return useSWR<InvitationLinkRes>(
    key,
    () => spaceService.generateInviteLink({ workspaceId: workspaceId! }),
    {
      // 不要自动重新验证，避免重复请求
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateOnMount: true,
      // 缓存邀请链接较长时间，因为它们通常不会频繁变化
      dedupingInterval: 60000, // 1分钟内的重复请求会被去重
    }
  )
}

/**
 * 手动触发生成邀请链接Hook (保留兼容性)
 * @returns SWR mutation对象，包含trigger（生成函数）、isMutating（加载状态）、error（错误信息）
 */
export const useGenerateInviteLinkMutation = () => {
  return useSWRMutation<
    InvitationLinkRes,
    Error,
    string,
    GenerateInviteLinkRequest
  >(API_PATHS.GENERATE_INVITE_LINK, (_, { arg }) =>
    spaceService.generateInviteLink(arg)
  )
}

/**
 * 获取邀请信息Hook
 * @param code 邀请码
 * @returns SWR查询对象，包含邀请信息数据
 */
export const useInviteMsg = (code?: string) => {
  // 构建唯一的key，只有当code存在时才进行请求
  const key = code ? [API_PATHS.GET_INVITE_MSG, code] : null

  return useSWR<InviteMsgRes>(
    key,
    () => spaceService.getInviteMsg({ code: code! }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateOnMount: true,
    }
  )
}

/**
 * 接受邀请Hook
 * @returns SWR mutation对象，包含trigger（接受函数）、isMutating（加载状态）、error（错误信息）
 */
export const useAcceptInvite = () => {
  return useSWRMutation<void, Error, string, AcceptInviteRequest>(
    API_PATHS.ACCEPT_INVITE,
    (_, { arg }) => spaceService.acceptInvite(arg)
  )
}

/**
 * 转让工作区所有权Hook
 * @returns SWR mutation对象，包含trigger（转让函数）、isMutating（加载状态）、error（错误信息）
 */
export const useTransferWorkspace = () => {
  return useSWRMutation<boolean, Error, string, TransferWorkspaceRequest>(
    API_PATHS.TRANSFER,
    (_, { arg }) => spaceService.transferWorkspace(arg)
  )
}

/**
 * 删除工作区Hook
 * @returns SWR mutation对象，包含trigger（删除函数）、isMutating（加载状态）、error（错误信息）
 */
export const useDeleteWorkspace = () => {
  return useSWRMutation<boolean, Error, string, DeleteWorkspaceRequest>(
    API_PATHS.DELETE,
    (_, { arg }) => spaceService.deleteWorkspace(arg)
  )
}

/**
 * 退出工作区Hook
 * @returns SWR mutation对象，包含trigger（退出函数）、isMutating（加载状态）、error（错误信息）
 */
export const useLeaveWorkspace = () => {
  return useSWRMutation<boolean, Error, string, LeaveWorkspaceRequest>(
    API_PATHS.LEAVE,
    (_, { arg }) => spaceService.leaveWorkspace(arg)
  )
}
